import StructureIdList from "../../js/struct/StructureIdList.js";
import {StructureId} from "../../js/struct/StructureId.js";

export default class StructureForms {
    /** @type {String} */
    #parent;
    /**
     * While there could be multiple forms, we need to choose a single structure to show to the user - the one
     * that represents the whole set.
     * @type{StructureId}
     */
    #representative;
    /** @type {StructureIdList} */
    #forms;
    /** @type {{stereo: boolean, protonation: boolean}} */
    #optionsSelected;

    /**
     * @param {String} parent
     * @param {StructureIdList} forms
     * @param {{stereo: boolean, protonation: boolean}} optionsSelected
     * @param {StructureId | null} representative
     */
    constructor(parent, forms, optionsSelected, representative) {
        this.#parent = parent;
        this.#forms = forms;
        this.#optionsSelected = optionsSelected;
        this.#representative = representative;
    }

    /**
     * @param {{stereo: boolean, protonation: boolean}} optionsSelected
     * @return {StructureForms}
     */
    withSelectedOptions(optionsSelected) {
        return new StructureForms(this.#parent, this.#forms, optionsSelected, this.representative);
    }

    /** @param {StructureId} id */
    addForm(id){
        this.#forms.addForm(id)
    }

    /** @param {{stereo: boolean, protonation: boolean}} options */
    setSelected(options){
        this.#optionsSelected = options;
    }

    /** @returns {StructureId} */
    get representative() {
        return this.#representative;
    }

    get parent() {
        return this.#parent;
    }

    /** @returns {string[]} */
    get formUids() {
        return this.#forms.toUids();
    }

    get selectedUidsDependsOnOptions() {
        const selectedForms = [];
        const isStereoIsomersInclude = this.#optionsSelected.stereo;
        const isProtonationIsomersInclude = this.#optionsSelected.protonation;
        for (let i = 0; i < this.#forms.size(); i++){
            const form = this.#forms.get(i);
            if (isStereoIsomersInclude && isProtonationIsomersInclude){
                selectedForms.push(form)
            } else if (!isStereoIsomersInclude && isProtonationIsomersInclude){
                if (form.stereo === this.#representative.stereo){
                    selectedForms.push(form);
                }
            } else if (!isProtonationIsomersInclude && isStereoIsomersInclude){
                if (form.protonation === this.#representative.protonation){
                    selectedForms.push(form)
                }
            } else {
                if (form.protonation === this.#representative.protonation && form.stereo === this.#representative.stereo){
                    selectedForms.push(form)
                }
            }
        }
        return new StructureIdList(selectedForms).toUids();
    }

    /** @returns {number} */
    get sizeSelected() {
        return this.selectedUidsDependsOnOptions.length;
    }

    get selectedOptions() {
        return this.#optionsSelected;
    }

    get size() {
        return this.#forms.size();
    }

    /**
     * @param {{parent: String, forms: []}[]} json
     * @param {StructureId[]} structureIds
     * @returns {StructureForms[]}
     */
    static parseJsonList(json, structureIds = []){
        const forms = [];
        for (const e of json) {
            const representativeId = structureIds.find(s => s.parent === e.parent).id;
            const representative = StructureId.parseServerJson(e.forms.find(s => s.id === representativeId));
            forms.push(new StructureForms(e.parent, new StructureIdList(StructureId.parseServerJsonList(e.forms)),
                {stereo: true, protonation: true}, representative));
        }
        return forms;
    }
}